interface ErrorOverlayProps {
  status: number
  onDismiss: () => void
}

const messages: Record<number, { title: string; body: string }> = {
  401: { title: 'Sign in required', body: 'Please sign in to continue.' },
  403: { title: 'Not permitted', body: "You don't have access to this page." },
  404: { title: 'Page not found', body: "The page you're looking for doesn't exist or has moved." },
  422: { title: 'Something went wrong', body: "The request couldn't be processed. Please try again." },
  429: { title: 'Slow down', body: 'Too many requests. Please wait a moment and try again.' },
  500: { title: 'Server error', body: 'Something went wrong on our end. Please try again shortly.' },
  503: { title: 'Unavailable', body: "The service is temporarily unavailable. We'll be back shortly." },
}

export function ErrorOverlay({ status, onDismiss }: ErrorOverlayProps) {
  const { title, body } = messages[status] ?? {
    title: 'Unexpected error',
    body: 'An unexpected error occurred. Please refresh and try again.',
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="error-overlay-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-page-bg/80 backdrop-blur-sm"
    >
      <div className="w-full max-w-sm bg-surface-bg border border-border-divider rounded-xl shadow-sm px-8 py-10 text-center">
        <p className="text-xs uppercase tracking-widest text-accent font-light mb-4">
          Error {status}
        </p>
        <h2
          id="error-overlay-title"
          className="font-serif text-2xl font-medium text-heading mb-3"
        >
          {title}
        </h2>
        <p className="text-sm text-muted font-light leading-relaxed mb-8">{body}</p>
        <div className="flex flex-col gap-3">
          <button
            onClick={() => window.location.reload()}
            className="w-full py-3 bg-btn-filled-bg text-btn-filled-text text-sm font-light tracking-wide rounded-full hover:bg-btn-filled-hover-bg transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
          >
            Reload page
          </button>
          <button
            onClick={onDismiss}
            className="w-full py-3 text-sm font-light text-muted hover:text-body transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 rounded-full"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  )
}
